import React, { useLayoutEffect, useState } from 'react';
import { Platform, Pressable, Text as RNText, ScrollView, StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { DevDemoStackParamList } from '@/feature/devsettings/DevDemoStack';
import { designSystem } from '@/styles/styles';

type Nav = NativeStackNavigationProp<DevDemoStackParamList>;

type ScrollEdgeEffect = 'automatic' | 'hard' | 'soft' | 'hidden';

const EFFECTS: ScrollEdgeEffect[] = ['automatic', 'hard', 'soft', 'hidden'];

const EFFECT_NOTES: Record<ScrollEdgeEffect, string> = {
    automatic: 'System default — the OS decides the style based on context',
    hard: 'Hard cutoff with a visible dividing line at the scroll edge',
    soft: 'Soft fade at the scroll edge, no hard line',
    hidden: 'No scroll edge effect at all',
};

const ITEMS = Array.from({ length: 22 }, (_, i) => `Item ${i + 1}`);

export const NativeHeaderScrollEdgeDemoScreen = () => {
    const navigation = useNavigation<Nav>();
    const [topEffect, setTopEffect] = useState<ScrollEdgeEffect>('automatic');

    useLayoutEffect(() => {
        navigation.setOptions({
            scrollEdgeEffects: { top: topEffect },
        });
    }, [navigation, topEffect]);

    const scrollEdgeEffectsLabel = `scrollEdgeEffects: { top: '${topEffect}' }`;

    return (
        <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scroll}
            contentInsetAdjustmentBehavior="automatic"
        >
            <Box mb="s16">
                <Text variant="labelSmall" color="neutral400" mb="s8">
                    Top scroll edge effect (iOS 26+)
                </Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <Box flexDirection="row" gap="s8">
                        {EFFECTS.map(effect => (
                            <Pressable
                                key={effect}
                                onPress={() => setTopEffect(effect)}
                                style={[styles.chip, topEffect === effect && styles.chipActive]}
                            >
                                <RNText style={[styles.chipText, topEffect === effect && styles.chipTextActive]}>
                                    {effect}
                                </RNText>
                            </Pressable>
                        ))}
                    </Box>
                </ScrollView>
            </Box>

            <Box borderRadius="r8" padding="s16" backgroundColor={ds.colorToken.background.neutral.subtler} mb="s16">
                <Text variant="labelSmall" color="neutral400" mb="s4">
                    Active options
                </Text>
                <RNText style={styles.code}>{scrollEdgeEffectsLabel}</RNText>
            </Box>

            <Box borderRadius="r8" padding="s12" backgroundColor={ds.colorToken.background.neutral.subtler} mb="s16">
                <Text variant="labelSmall" color="neutral400" mb="s4">
                    {topEffect}
                </Text>
                <Text variant="bodySmaller" color="neutral300">
                    {EFFECT_NOTES[topEffect]}
                </Text>
            </Box>

            <Box borderRadius="r8" padding="s12" backgroundColor={ds.colorToken.background.neutral.subtler} mb="s16">
                <Text variant="labelSmall" color="neutral400" mb="s4">
                    Note
                </Text>
                <Text variant="bodySmaller" color="neutral300">
                    This screen intentionally has no headerBlurEffect. Using both blurEffect and scrollEdgeEffects on
                    iOS 26 simultaneously may cause overlapping visual effects.
                </Text>
            </Box>

            <Box mb="s16">
                <Button
                    label="Push another level (see header animate)"
                    onPress={() => navigation.push('NavDemoHeaderScrollEdge')}
                />
            </Box>

            {ITEMS.map(item => (
                <Box
                    key={item}
                    padding="s16"
                    borderRadius="r8"
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    mb="s8"
                >
                    <Text variant="bodySmall" color="neutral300">
                        {item}
                    </Text>
                </Box>
            ))}
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    scrollView: {
        flex: 1,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
    scroll: {
        padding: 16,
        paddingBottom: 48,
    },
    code: {
        fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }),
        fontSize: 12,
        color: ds.colorValue.text.primary,
    },
    chip: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 16,
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
    chipActive: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
    },
    chipText: {
        fontSize: 11,
        color: designSystem.colors.neutral300,
        fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }),
    },
    chipTextActive: {
        color: ds.colorValue.text.primary,
    },
});
